import { motion } from 'framer-motion';

import { Card, cn } from '@llura/ui';
import { FlowSteps } from '@llura/constants';

import { COACH_LOOKS } from '../../../lib/coach-persona';
import type { CoachGender } from '../../../stores/coach.store';
import { useStepTiming } from '../../../lib/analytics';
import { useStrings } from '../../../hooks/useStrings';

interface LookStepProps {
  gender: CoachGender;
  value: string | null;
  onSelect: (avatarUrl: string) => void;
}

export function LookStep({ gender, value, onSelect }: LookStepProps) {
  useStepTiming(FlowSteps.CoachConfiguratorLook);
  const { strings, interp } = useStrings();
  const looks = COACH_LOOKS[gender];
  const stepLabel = interp(strings.onboarding.stepLabel, {
    current: 3,
    total: strings.onboarding.totalSteps,
  });

  return (
    <motion.div
      initial={{ opacity: 0, x: 24 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -24 }}
      transition={{ duration: 0.4, ease: 'easeOut' }}
      className="space-y-6"
    >
      <header className="space-y-2 text-center">
        <p className="text-xs uppercase tracking-[0.3em] text-muted-foreground">{stepLabel}</p>
        <h2 className="font-serif text-3xl">{strings.onboarding.look.title}</h2>
        <p className="text-sm text-muted-foreground">{strings.onboarding.look.hint}</p>
      </header>

      <div className="grid grid-cols-3 gap-3">
        {looks.map((url, i) => {
          const active = value === url;
          return (
            <Card
              key={url + i}
              role="button"
              tabIndex={0}
              onClick={() => onSelect(url)}
              onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && onSelect(url)}
              className={cn(
                'overflow-hidden rounded-2xl border-border bg-card p-0 transition-all hover:border-primary/60 cursor-pointer',
                active && 'border-primary glow-primary',
              )}
            >
              <img
                src={url}
                alt=""
                loading="lazy"
                className="aspect-square w-full object-cover"
              />
            </Card>
          );
        })}
      </div>
    </motion.div>
  );
}
